// Project files in and out. GPL-3.0-or-later.
import { readProject, writeProject, fileName, newId, NOTE, ProjectError } from "@dipfield/project";
import { t, tn } from "../i18n/index.js";
import { download, esc } from "../lib/dom.js";
import { toast } from "../lib/toast.js";
import { load, project } from "../state/store.js";
import { APP, ENGINES } from "../state/engines.js";

const SOURCE: Record<string, string> = { dipfield: "DipField 1", "dipfield-slope": "DipField Slope 0.1" };

/** Open a project file (Suite, DipField or DipField Slope). The opened project gets a new
 *  id, so it never overwrites a project already kept in this browser. */
export function openProjectText(text: string, name: string) {
  try {
    const { project: p, source } = readProject(text);
    p.meta.id = newId();
    if (source !== "suite") p.meta.name = name.replace(/\.(dfsuite\.)?json$/i, "").trim() || p.meta.name;
    load(p);
    const notes = p.meta.migratedFrom?.notes ?? [];
    let msg = t("Opened {name}", { name: esc(p.meta.name) }) + " · " + tn(p.data.planes.length, "{n} row", "{n} rows") + ", " + tn(p.sets.length, "{n} set", "{n} sets");
    if (source !== "suite") msg += " · " + t("converted from {app}", { app: SOURCE[source] });
    if (notes.includes(NOTE.densityFisher) || notes.includes(NOTE.coneRule)) msg += " · " + t("it used an older density setting and now uses Fisher (DIPS), so values may differ slightly from what was saved");
    if (notes.includes(NOTE.toppleContactRules)) msg += " · " + t("toppling now uses the contact rules, so a toppling FS can be lower than DipField Slope gave");
    toast(msg);
  } catch (e) {
    const code = e instanceof ProjectError ? e.code : "not-a-project";
    const why = { "not-json": t("it is not a JSON file"), "not-a-project": t("it is not a DipField Suite, DipField or DipField Slope project"),
      "newer-version": t("it was saved by a newer version of DipField Suite"), units: t("it states units other than kN, kPa and m") }[code];
    toast(t("Could not open {name}: {why}", { name: esc(name), why }));
  }
}

export function saveProjectFile() {
  const p = project();
  download(fileName(p), writeProject(p, { app: APP, engines: ENGINES }), "application/json");
}

/** An SVG figure as a PNG at twice its size. */
export function svgToPng(svg: string, name: string) {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas"); c.width = img.width * 2; c.height = img.height * 2;
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => { if (b) download(name, b); });
  };
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}
